import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type BuilderUnit } from '../../api';

type Format = 'aiken' | 'gift';
type Issue = { line: number; message: string };
type Question = {
  line: number; title?: string; type: string; textHtml: string; feedbackHtml: string | null;
  weight: number; multipleAnswers: boolean; answers: unknown; correctAnswers: unknown;
  pointRules: { type: string; text: string; points: number }[] | null;
};
type Preview = { format: Format; sourceHash: string; questions: Question[]; errors: Issue[]; warnings: Issue[]; count: number };
type Validated = { source: string; format: Format; importId: string; preview: Preview };
type Imported = { imported: number; questionIds: number[]; replayed: boolean };
const MAX_BYTES = 200_000;
const samples: Record<Format, string> = {
  aiken: 'Which number is even?\nA. 3\nB. 4\nC. 5\nANSWER: B\n\nWhich color is made by mixing blue and yellow?\nA. Green\nB. Purple\nC. Orange\nANSWER: A',
  gift: 'Which number is even? { ~3 =4 ~5 }\n\nA triangle has three sides. {TRUE}\n\nExplain how you would welcome a new colleague. {}',
};
const labels: Record<string, string> = {
  multiple_choice: 'Multiple choice', fill_the_gap: 'Fill the gap', drag_and_drop: 'Matching', freetext: 'Written answer',
};
// Imported HTML is deliberately presented as text. Decode only text entities;
// never attach parsed source markup to the page or load resources from it.
function plain(html: string): string {
  const entities: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '')
    .replace(/&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (entity, code: string) => {
      if (code[0] !== '#') return entities[code.toLowerCase()] ?? entity;
      const value = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : Number(code.slice(1));
      return value > 0 && value <= 0x10ffff ? String.fromCodePoint(value) : entity;
    }).trim();
}
function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
function strings(value: unknown): string[] { return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []; }
function pairs(value: unknown): { left: string; right: string }[] {
  const p = record(value), left = strings(p.left), right = strings(p.right);
  return left.map((label, index) => ({ left: label, right: right[index] ?? '' }));
}
const normalized = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();

function QuestionPreview({ question, index }: { question: Question; index: number }) {
  const q = question, answers = record(q.answers), correct = record(q.correctAnswers);
  const correctOptions = strings(correct.possible).map(normalized);
  const markedGaps = [...q.textHtml.matchAll(/\[([^\]]+)\]/g)].map(m => plain(m[1]));
  const stem = q.type === 'fill_the_gap' ? q.textHtml.replace(/\[[^\]]+\]/g, '_____') : q.textHtml;
  return <article className="panel" style={{ overflowWrap: 'anywhere' }}>
    <p className="muted small">Question {index + 1} · Line {q.line} · {labels[q.type] ?? q.type.replace(/_/g, ' ')} · {q.weight} {q.weight === 1 ? 'mark' : 'marks'}</p>
    {q.title && <p className="muted small">Source title: {q.title}</p>}
    <p style={{ whiteSpace: 'pre-wrap' }}><strong>{plain(stem)}</strong></p>
    {q.type === 'multiple_choice' && <>
      <p>{q.multipleAnswers ? 'Select every correct answer. Full marks require the exact set.' : 'Select one answer.'}</p>
      <ol type="A">{strings(answers.possible).map((option, i) => <li key={i}>{option}{correctOptions.includes(normalized(option)) && <strong> — Correct</strong>}</li>)}</ol>
    </>}
    {q.type === 'drag_and_drop' && <><p><strong>Correct matches</strong></p><table><thead><tr><th>Prompt</th><th>Answer</th></tr></thead><tbody>
      {pairs(correct.pairs ?? answers.pairs).map((pair, i) => <tr key={i}><td>{pair.left}</td><td>{pair.right}</td></tr>)}
    </tbody></table></>}
    {q.type === 'fill_the_gap' && <><p><strong>Answers in gap order</strong></p><ol>
      {(markedGaps.length ? markedGaps : Object.values(record(answers.gaps)).flatMap(strings)).map((answer, i) => <li key={i}>{answer}</li>)}
    </ol></>}
    {q.type === 'freetext' && (q.pointRules?.length ? <><p><strong>Accepted answers</strong></p><ul>{q.pointRules.map((rule, i) => <li key={i}>{rule.text} ({rule.type === 'exact' ? 'exact answer' : 'contains'}, {rule.points} marks)</li>)}</ul></> : <p className="notice">An educator must mark this written answer.</p>)}
    {q.feedbackHtml && <p style={{ whiteSpace: 'pre-wrap' }}>Feedback: {plain(q.feedbackHtml)}</p>}
  </article>;
}

export function QuestionImport({ mode = 'admin' }: { mode?: 'admin' | 'instructor' }) {
  const { id, unitId } = useParams();
  const base = mode === 'instructor' ? '/instructor/courses' : '/admin/courses';
  const back = `${base}/${id}/edit/units/${unitId}`;
  const unitEndpoint = `/admin/builder/${id}/units/${unitId}`;
  const endpoint = `${unitEndpoint}/question-import`;
  const [unit, setUnit] = useState<BuilderUnit['unit'] | null>(null);
  const [format, setFormat] = useState<Format>('aiken'), [source, setSource] = useState('');
  const [validated, setValidated] = useState<Validated | null>(null), [imported, setImported] = useState<Imported | null>(null);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [loading, setLoading] = useState(true);
  const operation = useRef(0);
  const importIdentity = useRef<{ source: string; format: Format; importId: string } | null>(null);
  useEffect(() => {
    const current = ++operation.current;
    importIdentity.current = null;
    setUnit(null); setSource(''); setValidated(null); setImported(null); setError(''); setBusy(false); setLoading(true);
    void api.get<BuilderUnit>(unitEndpoint).then(result => {
      if (current === operation.current) setUnit(result.unit);
    }).catch(e => { if (current === operation.current) setError((e as Error).message); })
      .finally(() => { if (current === operation.current) setLoading(false); });
    return () => { operation.current += 1; };
  }, [unitEndpoint]);
  const changeSource = (value: string) => { importIdentity.current = null; setSource(value); setValidated(null); setError(''); };
  const upload = async (file?: File) => {
    if (!file) return;
    const current = ++operation.current;
    importIdentity.current = null;
    setValidated(null); setError(''); setBusy(true);
    try {
      if (!/\.txt$/i.test(file.name)) throw new Error('Choose a plain text (.txt) file.');
      if (file.size > MAX_BYTES) throw new Error('The file is too large. Split it into files of 200 KB or less.');
      let text: string;
      try { text = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer()); }
      catch { throw new Error('This file is not valid UTF-8 text. Save it as UTF-8 and try again.'); }
      if (current === operation.current) setSource(text);
    } catch (e) { if (current === operation.current) setError((e as Error).message); }
    finally { if (current === operation.current) setBusy(false); }
  };
  const validate = async () => {
    const current = ++operation.current;
    const prior = importIdentity.current;
    const importId = prior?.source === source && prior.format === format ? prior.importId : crypto.randomUUID();
    importIdentity.current = { source, format, importId };
    setBusy(true); setError(''); setValidated(null);
    try {
      if (new TextEncoder().encode(source).length > MAX_BYTES) throw new Error('The text is too large. Split it into batches of 200 KB or less.');
      const preview = await api.post<Preview>(`${endpoint}/preview`, { format, source });
      if (current === operation.current) setValidated({ source, format, importId, preview });
    } catch (e) { if (current === operation.current) setError((e as Error).message); }
    finally { if (current === operation.current) setBusy(false); }
  };
  const save = async () => {
    if (!validated || validated.preview.errors.length || !validated.preview.count) return;
    const current = ++operation.current;
    setBusy(true); setError('');
    const body = { format: validated.format, source: validated.source, sourceHash: validated.preview.sourceHash, importId: validated.importId };
    try {
      let result: Imported;
      try { result = await api.post<Imported>(endpoint, body); }
      catch (e) {
        const err = e as ApiError;
        if (current !== operation.current) return;
        if (err.status !== 409 || !err.body?.needsConfirmation || !confirm(err.message)) throw err;
        result = await api.post<Imported>(endpoint, { ...body, force: true });
      }
      if (current === operation.current) { setImported(result); setValidated(null); setSource(''); }
    } catch (e) { if (current === operation.current) setError((e as Error).message); }
    finally { if (current === operation.current) setBusy(false); }
  };
  const preview = validated?.preview;
  return <>
    <nav className="crumbs"><Link to={back}>Back to assessment</Link></nav>
    <h1 className="h1">Import questions</h1>
    <p className="muted">Upload or paste questions, validate them, then review the answers before adding the whole batch to this test.</p>
    {loading && <p>Loading assessment…</p>}
    {unit && <p>Destination: <strong>{unit.name}</strong> · {unit.questions?.length ?? 0} existing questions</p>}
    {error && <p className="auth__error" role="alert">{error}</p>}
    {unit && unit.type !== 'test' && <p className="notice">Bulk question import is available for tests. <Link to={back}>Return to this unit</Link>.</p>}
    {imported ? <section className="panel" role="status">
      <h2>{imported.imported} {imported.imported === 1 ? 'question' : 'questions'} imported</h2>
      <p>The questions have been added to {unit?.name}. Review them and adjust marks in the assessment editor.</p>
      <Link className="btn btn--primary" to={back}>Review assessment</Link>
    </section> : unit?.type === 'test' && <>
      <form className="panel form" style={{ maxWidth: 'none', width: '100%', minWidth: 0 }} onSubmit={e => { e.preventDefault(); void validate(); }}>
        <fieldset disabled={busy} style={{ minWidth: 0, width: '100%', margin: 0, padding: 0, border: 0, display: 'grid', gap: '0.8rem' }}>
          <legend style={{ marginBottom: '0.8rem' }}>1. Add your questions</legend>
          <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}><label htmlFor="question-import-format">Import format</label><select id="question-import-format" style={{ minWidth: 0, width: '100%', maxWidth: '100%' }} value={format} onChange={e => { importIdentity.current = null; setFormat(e.target.value as Format); setValidated(null); setError(''); }}>
            <option value="aiken">AIKEN — single-answer multiple choice</option><option value="gift">GIFT — multiple choice, true/false, gaps, matching and written answers</option>
          </select></div>
          <p className="muted small">Plain UTF-8 text, up to 200 questions and 200 KB per batch. Unsupported GIFT syntax is reported during validation.</p>
          <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}><label htmlFor="question-import-file">Upload a text file</label><input id="question-import-file" style={{ minWidth: 0, width: '100%', maxWidth: '100%' }} type="file" accept=".txt,text/plain" onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; void upload(file); }} /></div>
          <div style={{ display: 'grid', gap: '0.25rem', minWidth: 0 }}><label htmlFor="question-import-source">Question text</label><textarea id="question-import-source" style={{ minWidth: 0, width: '100%', maxWidth: '100%' }} value={source} rows={14} spellCheck={false} onChange={e => changeSource(e.target.value)} aria-describedby="question-import-help" /></div>
          <p className="muted small" id="question-import-help">Separate questions with a blank line. Imported questions start at one mark each; review every answer key before saving.</p>
          <details><summary>See a {format.toUpperCase()} example</summary>
            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{samples[format]}</pre>
            <button type="button" className="btn btn--ghost" onClick={() => changeSource(samples[format])}>{source.trim() ? 'Replace text with this example' : 'Use this example'}</button>
          </details>
          <button className="btn btn--primary" disabled={busy || !source.trim()}>{busy ? 'Working…' : 'Validate and preview'}</button>
        </fieldset>
      </form>
      {preview && <section aria-labelledby="import-preview-heading">
        <h2 id="import-preview-heading">2. Review your batch</h2>
        <p>{preview.count} {preview.count === 1 ? 'question' : 'questions'} ready for review. {preview.errors.length ? 'Resolve every error, then validate again. No questions have been saved.' : 'Check the question text, correct answers and marking before importing.'}</p>
        {preview.errors.length > 0 && <div className="auth__error" role="alert"><strong>Fix these errors</strong><ul>{preview.errors.map((issue, i) => <li key={i}>Line {issue.line}: {issue.message}</li>)}</ul></div>}
        {preview.warnings.length > 0 && <div className="notice notice--warn"><strong>Review these notes</strong><ul>{preview.warnings.map((issue, i) => <li key={i}>Line {issue.line}: {issue.message}</li>)}</ul></div>}
        {preview.questions.map((question, i) => <QuestionPreview key={`${question.line}-${i}`} question={question} index={i} />)}
        <div className="panel">
          <p>Import adds every valid question in this batch to the end of the test. Existing questions and exam settings are preserved.</p>
          <button className="btn btn--primary" disabled={busy || !!preview.errors.length || !preview.count} onClick={() => void save()}>{busy ? 'Working…' : `Import ${preview.count} ${preview.count === 1 ? 'question' : 'questions'}`}</button>
        </div>
      </section>}
    </>}
  </>;
}
